import Layout from "@/Components/Layout/Screen";
import RequiredAuth from "@/Components/RequiredAuth";
import RequiredUnauth from "@/Components/RequiredUnauth";
import PageLoader from "@/Components/PageLoader";
import DocumentMeta from "./DocumentMeta";
import { canAccessSettings } from "./moduleAccess";
import { useLoggedUserStore } from "@/Store";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

const ForgotPassword = lazy(
  () => import("@/Modules/Auth/ForgotPassword/Screen")
);
const Login = lazy(() => import("@/Modules/Auth/Login/Screen"));
const SignUp = lazy(() => import("@/Modules/Auth/SignUp/Screen"));
const ResetPassword = lazy(() => import("@/Modules/Auth/ResetPassword/Screen"));
const CreatePassword = lazy(
  () => import("@/Modules/Auth/CreatePassword/Screen")
);
const ContactCreator = lazy(() => import("@/Modules/Contacts/ContactCreator"));
const ContactsDetail = lazy(() => import("@/Modules/Contacts/ContactDetail"));
const ContactsManagement = lazy(
  () => import("@/Modules/Contacts/ContactManagement")
);
const CustomerCreator = lazy(
  () => import("@/Modules/Customers/CustomerCreator/Screen")
);
const CustomerDetail = lazy(
  () => import("@/Modules/Customers/CustomerDetail/Screen")
);
const CustomerManagement = lazy(
  () => import("@/Modules/Customers/CustomerManagement/Screen")
);
const EmployeeManagement = lazy(
  () => import("@/Modules/Employees/EmployeeManagement")
);
const EmployeeCreator = lazy(
  () => import("@/Modules/Employees/EmployeeCreator")
);
const EmployeeDetail = lazy(() => import("@/Modules/Employees/EmployeeDetail"));
const WorkScheduleManagement = lazy(
  () => import("@/Modules/WorkSchedules/WorkScheduleManagement")
);
const WorkScheduleCreator = lazy(
  () => import("@/Modules/WorkSchedules/WorkScheduleCreator")
);
const WorkScheduleDetail = lazy(
  () => import("@/Modules/WorkSchedules/WorkScheduleDetail")
);
const HolidayManagement = lazy(
  () => import("@/Modules/Holidays/HolidayManagement")
);
const HolidayCreator = lazy(() => import("@/Modules/Holidays/HolidayCreator"));
const HolidayDetail = lazy(() => import("@/Modules/Holidays/HolidayDetail"));
const TimeClockDeviceManagement = lazy(
  () => import("@/Modules/TimeClockDevices/TimeClockDeviceManagement")
);
const TimeClockPunchManagement = lazy(
  () => import("@/Modules/TimeClockPunches/TimeClockPunchManagement")
);
const TimeClockPunchCreator = lazy(
  () => import("@/Modules/TimeClockPunches/TimeClockPunchCreator")
);
const TimeClockAdjustmentQueue = lazy(
  () => import("@/Modules/TimeClockPunches/TimeClockAdjustmentQueue")
);
const AbsenceJustificationQueue = lazy(
  () => import("@/Modules/TimeClockPunches/AbsenceJustificationQueue")
);
const TimesheetPeriodQueue = lazy(
  () => import("@/Modules/TimeClockPunches/TimesheetPeriodQueue")
);
const TimesheetPeriodDetail = lazy(
  () => import("@/Modules/TimeClockPunches/TimesheetPeriodDetail")
);
const TimeClockPunchDetail = lazy(
  () => import("@/Modules/TimeClockPunches/TimeClockPunchDetail")
);
const MyTimeClock = lazy(
  () => import("@/Modules/TimeClockPunches/MyTimeClock")
);
const MyTimeClockReceipt = lazy(
  () => import("@/Modules/TimeClockPunches/MyTimeClockReceipt")
);
const TimeClockTerminalApp = lazy(
  () => import("@/Modules/TimeClockTerminal/TimeClockTerminalApp")
);
const PublicFacialTerminal = lazy(
  () => import("@/Modules/TimeClockTerminal/PublicFacialTerminal")
);
const PublicTimeClockSettings = lazy(
  () => import("@/Modules/TimeClockTerminal/PublicTimeClockSettings")
);
const FacialEnrollmentInvite = lazy(
  () => import("@/Modules/TimeClockTerminal/FacialEnrollmentInvite")
);
const DepartmentCreator = lazy(
  () => import("@/Modules/Departments/DepartmentCreator")
);
const DepartmentDetail = lazy(
  () => import("@/Modules/Departments/DepartmentDetail")
);
const DepartmentsManagement = lazy(
  () => import("@/Modules/Departments/DepartmentsManagement/Screen")
);
const Home = lazy(() => import("@/Modules/Home/Screen"));
const Overview = lazy(() => import("@/Modules/Overview/Screen"));
const NoMatch = lazy(() => import("@/Modules/NoMatch/Screen"));
const Profile = lazy(() => import("@/Modules/Profile/Screen"));
const DocumentsManagement = lazy(
  () => import("@/Modules/Documents/DocumentsManagement")
);
const ProjectCreator = lazy(() => import("@/Modules/Projects/ProjectCreator"));
const ProjectDetail = lazy(() => import("@/Modules/Projects/ProjectDetail"));
const ProjectManagement = lazy(
  () => import("@/Modules/Projects/ProjectManagement")
);
const UserDetail = lazy(() => import("@/Modules/Users/UserDetail/Screen"));
const UsersManagement = lazy(
  () => import("@/Modules/Users/UsersManagement/Screen")
);
const CompanyManagement = lazy(
  () => import("@/Modules/Companies/CompanyManagement")
);
const CompanyCreator = lazy(() => import("@/Modules/Companies/CompanyCreator"));
const CompanyDetail = lazy(() => import("@/Modules/Companies/CompanyDetail"));
const AccountManagement = lazy(
  () => import("@/Modules/Accounts/AccountManagement")
);
const AccountDetail = lazy(() => import("@/Modules/Accounts/AccountDetail"));
const VacationCreator = lazy(
  () => import("@/Modules/Vacations/VacationCreator")
);
const VacationDetail = lazy(() => import("@/Modules/Vacations/VacationDetail"));
const VacationManagement = lazy(
  () => import("@/Modules/Vacations/VacationManagement")
);
const SharedView = lazy(() => import("@/Modules/Shared/SharedView"));
const LeadsBoard = lazy(() => import("@/Modules/Leads/LeadsBoard"));
const SettingsScreen = lazy(() => import("@/Modules/Settings/Screen"));
const SubscriptionsManagement = lazy(
  () => import("@/Modules/Subscriptions/SubscriptionsManagement")
);
const FinanceOverview = lazy(() => import("@/Modules/Finance/FinanceOverview"));
const InvoicesManagement = lazy(
  () => import("@/Modules/Invoices/InvoicesManagement")
);
const InvoiceCreator = lazy(() => import("@/Modules/Invoices/InvoiceCreator"));
const InvoiceDetail = lazy(() => import("@/Modules/Invoices/InvoiceDetail"));
const InvoicePrint = lazy(() => import("@/Modules/Invoices/InvoicePrint"));
const InvoiceDraftEditor = lazy(
  () => import("@/Modules/Invoices/InvoiceDraftEditor")
);
const BillingContractManagement = lazy(
  () => import("@/Modules/BillingContracts/BillingContractManagement")
);
const BillingContractCreator = lazy(
  () => import("@/Modules/BillingContracts/BillingContractCreator")
);
const BillingContractDetail = lazy(
  () => import("@/Modules/BillingContracts/BillingContractDetail")
);
const KanbanBoards = lazy(() => import("@/Modules/Kanban/KanbanBoards"));
const KanbanBoardView = lazy(() => import("@/Modules/Kanban/KanbanBoardView"));
const AuditManagement = lazy(() => import("@/Modules/Audit/AuditManagement"));
const NotificationsManagement = lazy(
  () => import("@/Modules/Notifications/NotificationsManagement")
);
const MyDay = lazy(() => import("@/Modules/MyDay"));
const BillingScreen = lazy(() => import("@/Modules/Billing"));
const GeniaPayCallback = lazy(
  () => import("@/Modules/Integrations/GeniaPayCallback")
);
const GeniaPayRegisterCallback = lazy(
  () => import("@/Modules/Integrations/GeniaPayRegisterCallback")
);
const GeniaPayAppScreen = lazy(
  () => import("@/Modules/Integrations/GeniaPayApp")
);

export default function App() {
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const isPlatformStaff =
    loggedUser?.type === "superadmin" || loggedUser?.type === "support";
  return (
    <div>
      <DocumentMeta />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Rota pública de compartilhamento (sem login). */}
          <Route path="/s/:token" element={<SharedView />} />

          <Route path="/terminal" element={<TimeClockTerminalApp />} />

          <Route path="/facial/:code" element={<PublicFacialTerminal />} />

          <Route
            path="/cadastro-facial/:token"
            element={<FacialEnrollmentInvite />}
          />

          <Route element={<RequiredAuth />}>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/visao-geral" replace />} />
              <Route path="/inicio" element={<Home />} />
              <Route path="/visao-geral" element={<Overview />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/meu-dia" element={<MyDay />} />
              <Route
                path="/notificacoes"
                element={<NotificationsManagement />}
              />
              <Route path="/documentos" element={<DocumentsManagement />} />

              <Route path="/usuarios" element={<UsersManagement />} />
              <Route path="/usuarios/:id" element={<UserDetail />} />
              <Route
                path="/departamentos"
                element={<DepartmentsManagement />}
              />
              <Route path="/departamentos/:id" element={<DepartmentDetail />} />

              <Route path="/contatos" element={<ContactsManagement />} />
              <Route path="/contatos/criar" element={<ContactCreator />} />
              <Route path="/contatos/:id" element={<ContactsDetail />} />

              <Route path="/clientes" element={<CustomerManagement />} />
              <Route path="/clientes/criar" element={<CustomerCreator />} />
              <Route path="/clientes/:id" element={<CustomerDetail />} />

              <Route path="/funcionarios" element={<EmployeeManagement />} />
              <Route path="/funcionarios/criar" element={<EmployeeCreator />} />
              <Route path="/funcionarios/:id" element={<EmployeeDetail />} />

              <Route path="/jornadas" element={<WorkScheduleManagement />} />
              <Route path="/jornadas/criar" element={<WorkScheduleCreator />} />
              <Route path="/jornadas/:id" element={<WorkScheduleDetail />} />

              <Route path="/feriados" element={<HolidayManagement />} />
              <Route path="/feriados/criar" element={<HolidayCreator />} />
              <Route path="/feriados/:id" element={<HolidayDetail />} />

              <Route
                path="/terminais"
                element={<TimeClockDeviceManagement />}
              />

              <Route path="/meu-ponto" element={<MyTimeClock />} />
              <Route
                path="/meu-ponto/comprovante/:id"
                element={<MyTimeClockReceipt />}
              />

              <Route path="/ponto" element={<TimeClockPunchManagement />} />
              <Route
                path="/ponto/ajustes"
                element={<TimeClockAdjustmentQueue />}
              />
              <Route
                path="/ponto/justificativas"
                element={<AbsenceJustificationQueue />}
              />
              <Route
                path="/ponto/espelhos"
                element={<TimesheetPeriodQueue />}
              />
              <Route
                path="/ponto/espelhos/:employeeId"
                element={<TimesheetPeriodDetail />}
              />
              <Route path="/ponto/:id" element={<TimeClockPunchDetail />} />

              <Route
                path="/terminal-publico"
                element={<PublicTimeClockSettings />}
              />

              <Route path="/leads" element={<LeadsBoard />} />

              <Route path="/projetos" element={<ProjectManagement />} />
              <Route path="/projetos/criar" element={<ProjectCreator />} />
              <Route path="/projetos/:id" element={<ProjectDetail />} />

              <Route path="/assinantes" element={<SubscriptionsManagement />} />
              <Route path="/financeiro" element={<FinanceOverview />} />
              <Route path="/faturas" element={<InvoicesManagement />} />
              <Route path="/faturas/nova" element={<InvoiceCreator />} />
              <Route path="/faturas/:id" element={<InvoiceDetail />} />
              <Route path="/faturas/:id/imprimir" element={<InvoicePrint />} />
              <Route
                path="/faturas/:id/editar"
                element={<InvoiceDraftEditor />}
              />
              <Route
                path="/contratos-cobranca"
                element={<BillingContractManagement />}
              />
              <Route
                path="/contratos-cobranca/criar"
                element={<BillingContractCreator />}
              />
              <Route
                path="/contratos-cobranca/:id"
                element={<BillingContractDetail />}
              />

              <Route
                path="/integrations/geniapay/callback"
                element={<GeniaPayCallback />}
              />

              <Route path="/kanban" element={<KanbanBoards />} />
              <Route path="/kanban/:id" element={<KanbanBoardView />} />

              <Route path="/ferias" element={<VacationManagement />} />
              <Route path="/ferias/criar" element={<VacationCreator />} />
              <Route path="/ferias/:id" element={<VacationDetail />} />

              {["superadmin", "support", "owner", "admin"].includes(
                loggedUser?.type ?? ""
              ) && <Route path="/auditoria" element={<AuditManagement />} />}

              {canAccessSettings(loggedUser?.type) && (
                <>
                  <Route
                    path="/departamentos/criar"
                    element={<DepartmentCreator />}
                  />

                  <Route
                    path="/ponto/lancar"
                    element={<TimeClockPunchCreator />}
                  />

                  <Route path="/configuracoes" element={<SettingsScreen />} />
                </>
              )}

              {(isPlatformStaff || loggedUser?.type === "owner") && (
                <>
                  <Route path="/empresas" element={<CompanyManagement />} />
                  <Route path="/empresas/:id" element={<CompanyDetail />} />
                  {(loggedUser?.type === "owner" ||
                    loggedUser?.type === "superadmin") && (
                    <Route
                      path="/empresas/criar"
                      element={<CompanyCreator />}
                    />
                  )}
                </>
              )}
              {isPlatformStaff && (
                <>
                  <Route path="/contas" element={<AccountManagement />} />
                  <Route path="/contas/:id" element={<AccountDetail />} />
                  <Route
                    path="/plataforma/geniapay"
                    element={<GeniaPayAppScreen />}
                  />
                  <Route
                    path="/integrations/geniapay/register"
                    element={<GeniaPayRegisterCallback />}
                  />
                </>
              )}
              {loggedUser?.type === "owner" && (
                <Route path="/plano" element={<BillingScreen />} />
              )}
              <Route path="*" element={<NoMatch />} />
            </Route>
          </Route>

          <Route element={<RequiredUnauth />}>
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<SignUp />} />
            <Route path="/redefinir-senha" element={<ForgotPassword />} />
            <Route path="/resetar-senha" element={<ResetPassword />} />
            <Route path="/criar-senha" element={<CreatePassword />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}
